import React, { useEffect, useState } from 'react';
import { Box, Card } from '@mui/material';
import { Formalite } from '@europrocurement/l2d-domain';
import {
    DatatablePagination,
    FlexyDatatable,
    FlexyDatatableProps,
} from '@europrocurement/flexy-datatable';
import { useNavigate } from 'react-router';
import { Formality, useFormalitiesCtx } from '@europrocurement/l2d-modules';
import NoResultPreview from '../../components/NoResultPreview';
import TableRowActions from '../../components/TableRowActions';

type FormalityCompatibilitiesCardProps = {
    formalityId: number;
    onRemoveCompatibility: (compatibilityId: string | number) => void;
};

const FormalityCompatibilitiesCard: React.FunctionComponent<FormalityCompatibilitiesCardProps> =
    function (props) {
        const { formalityId, onRemoveCompatibility } = props;
        const navigate = useNavigate();
        const { getFormalityById } = useFormalitiesCtx();
        const formality = getFormalityById(formalityId);
        const [compatibilities, setCompatibilities] = useState<Array<Formality>>([]);
        const [page, setCurrentPage] = useState(0);
        const [totalItemsCount, setTotalItemsCount] = useState(0);
        const [itemsPerPage, setItemsPerPage] = useState(10);
        const pagination: DatatablePagination = {
            page,
            itemsPerPage,
            total: totalItemsCount,
            onPageChange: (newValue) => {
                setCurrentPage(newValue);
            },
            onItemsPerPageChange: (newValue) => {
                setCurrentPage(0);
                setItemsPerPage(newValue);
            },
        };

        const refreshList = () => {
            const firstItem = page * itemsPerPage;
            const items = formality?.combinabilities
                .map((itemId) => getFormalityById(itemId))
                .filter((item) => item !== undefined);
            setTotalItemsCount((items || []).length);
            setCompatibilities(
                (items || []).slice(firstItem, firstItem + itemsPerPage) as Formality[],
            );
        };

        // eslint-disable-next-line react-hooks/exhaustive-deps
        useEffect(refreshList, [formality, page, itemsPerPage]);

        if (!compatibilities.length) {
            return (
                <Card>
                    <NoResultPreview message="Cette formalité ne comporte aucune compatibilité" />
                </Card>
            );
        }

        const celStyle = {
            display: 'flex',
            alignItems: 'center',
            minHeight: '40px',
        };
        const columns = [
            {
                label: 'Nom',
                field: 'libelle',
                isDisplayed: true,
                render: (item: Formality) => <Box style={celStyle}>{item.label}</Box>,
            },
            {
                label: 'Catégorie',
                field: 'categorie',
                isDisplayed: true,
                render: (item: Formality) => <Box style={celStyle}>{item?.category?.label}</Box>,
            },
            {
                label: 'Actions',
                displayLabel: false,
                isDisplayed: true,
                render: (item: Formality) => (
                    <TableRowActions
                        itemId={item.id as unknown as string}
                        removeItem={() => {
                            onRemoveCompatibility(item?.id as unknown as string);
                        }}
                    />
                ),
            },
        ];

        const datatableProps: FlexyDatatableProps = {
            status: 'succeeded',
            data: compatibilities,
            hideColumnOptions: true,
            pagination,
            columns,
            onClickRow: (_: React.MouseEvent<HTMLElement>, { id }: Formalite) =>
                navigate(`/formalites/liste/${id}`),
        };

        return (
            <Card>
                <FlexyDatatable {...datatableProps} />
            </Card>
        );
    };

export default FormalityCompatibilitiesCard;
